/**
 * Shared authenticated fetch for the AE Tracker Functions API (/api/*).
 * Used by client.js and blobClient.js to avoid circular imports.
 */

let _getApiToken = null;

export function setApiTokenProvider(fn) {
  _getApiToken = fn;
}

// Impersonation ("assume identity") provider — returns { role, email, subId? } | null.
// When set, every API request carries the X-Impersonate-* headers so the backend
// resolves scope + capabilities as the assumed user. SuperAdmin-gated server-side.
// `subId` is the clinic-mailbox PERSON being viewed as (a shared mailbox owns no
// cases of its own); the server re-checks it against that clinic's roster.
let _getImpersonation = null;

export function setImpersonationProvider(fn) {
  _getImpersonation = fn;
}

// Clinic-mailbox sub-identity session token, sent as X-Sub-Session so the backend
// resolves caller.subId. sessionStorage ONLY (per-tab) — two staff sharing one
// machine must not ride each other's session via shared localStorage.
const SUB_SESSION_KEY = 'ae-sub-session';
export function setSubSession(token, name, clinic, jobTitle, subId) {
  try {
    if (token) sessionStorage.setItem(SUB_SESSION_KEY, JSON.stringify({ token, name: name || null, clinic: clinic || null, jobTitle: jobTitle || null, subId: subId || null }));
    else sessionStorage.removeItem(SUB_SESSION_KEY);
  } catch { /* storage disabled — header simply not sent */ }
}
export function clearSubSession() { setSubSession(null); }
function readSubSession() {
  try { return JSON.parse(sessionStorage.getItem(SUB_SESSION_KEY) || 'null'); } catch { return null; }
}
export function getSubSessionToken() { return readSubSession()?.token || null; }
export function getSubSessionName() { return readSubSession()?.name || null; }
export function getSubSessionClinic() { return readSubSession()?.clinic || null; }
export function getSubSessionJobTitle() { return readSubSession()?.jobTitle || null; }
export function getSubSessionId() { return readSubSession()?.subId || null; }

// The server times a PIN session out after 60 idle minutes, but the token stays
// in this tab. A lapsed session is announced here (by a coded 403, or by the
// SubSessionGuard's status check); the guard shows the PIN pad OVER the page —
// nothing unmounts, so typed input survives — and announces the renewal, which
// lets a caller that was refused simply retry.
export const SUB_SESSION_EXPIRED = 'SUB_SESSION_EXPIRED';
const EXPIRED_EVENT = 'ae:sub-session-expired';
const RENEWED_EVENT = 'ae:sub-session-renewed';
export function announceSubSessionExpired() { window.dispatchEvent(new Event(EXPIRED_EVENT)); }
export function announceSubSessionRenewed() { window.dispatchEvent(new Event(RENEWED_EVENT)); }
export function onSubSessionExpired(fn) {
  window.addEventListener(EXPIRED_EVENT, fn);
  return () => window.removeEventListener(EXPIRED_EVENT, fn);
}
/** Ask for the PIN (if not already asked) and resolve once it is re-entered. */
export function waitForSubSessionRenewal() {
  return new Promise(resolve => {
    window.addEventListener(RENEWED_EVENT, () => resolve(), { once: true });
    announceSubSessionExpired();
  });
}

// Re-authentication handler — fires an interactive MSAL redirect when the session
// is no longer valid. Registered by App.jsx from AuthContext. Idempotent: the
// handler itself guards against firing more than one redirect.
let _reauth = null;

export function setReauthHandler(fn) {
  _reauth = fn;
}

/** Resolve bearer token for multipart uploads and Auth bootstrap. */
export async function getApiTokenForRequest(accessToken) {
  if (accessToken) return accessToken;
  if (!_getApiToken) return null;
  return _getApiToken();
}

/** Impersonation headers (if assuming an identity) — for non-apiFetch callers
 *  such as multipart uploads, so the backend's read-only guard applies there too. */
export function getImpersonationHeaders() {
  const imp = _getImpersonation?.();
  if (!imp?.role || !imp?.email) return {};
  const headers = {
    'X-Impersonate-Role': imp.role,
    'X-Impersonate-Email': imp.email,
  };
  if (imp.subId) headers['X-Impersonate-Sub-Id'] = imp.subId;
  const anchor = imp.scopeAnchor;
  if (anchor?.role && anchor?.email) {
    headers['X-Impersonate-Scope-Role']  = anchor.role;
    headers['X-Impersonate-Scope-Email'] = anchor.email;
  }
  return headers;
}

/**
 * @param {string} path — e.g. '/api/cases'
 * @param {RequestInit & { accessToken?: string }} options
 */
export async function apiFetch(path, options = {}) {
  const { accessToken, timeoutMs = 45_000, ...fetchOpts } = options;
  const headers = { 'Content-Type': 'application/json', ...fetchOpts.headers };

  let token = accessToken || null;
  if (!token) {
    try {
      token = await getApiTokenForRequest();
    } catch (e) {
      // Silent token renewal failed — the session has expired. The token
      // provider has already started an interactive re-auth redirect, so the
      // page is navigating away. Don't fall through to an unauthenticated
      // request (that produced the misleading "no token provided" error).
      console.error('[apiFetch] Could not acquire API token — re-authenticating:', e.message);
      _reauth?.();
      throw new Error('Session expired — signing in again…');
    }
  }
  if (token) headers.Authorization = `Bearer ${token}`;
  else if (path.startsWith('/api/')) {
    console.error('[apiFetch] No API token — re-authenticating');
    _reauth?.();
    throw new Error('Session expired — signing in again…');
  }

  const subToken = getSubSessionToken();
  if (subToken && !headers['X-Sub-Session']) headers['X-Sub-Session'] = subToken;

  const imp = _getImpersonation?.();
  if (imp?.role && imp?.email) {
    // Explicit X-Impersonate-* on the call (e.g. switch-identity probe) wins over
    // the stored session — otherwise we'd re-send the current view, not the target.
    // The stored sub-identity belongs to the stored target, so it only rides along
    // when the call is NOT naming a target of its own.
    const targetIsStored = !headers['X-Impersonate-Role'] && !headers['X-Impersonate-Email'];
    if (!headers['X-Impersonate-Role'])  headers['X-Impersonate-Role']  = imp.role;
    if (!headers['X-Impersonate-Email']) headers['X-Impersonate-Email'] = imp.email;
    if (targetIsStored && imp.subId && !headers['X-Impersonate-Sub-Id']) {
      headers['X-Impersonate-Sub-Id'] = imp.subId;
    }
    if (!headers['X-Impersonate-Scope-Role'] && imp.scopeAnchor?.role && imp.scopeAnchor?.email) {
      headers['X-Impersonate-Scope-Role']  = imp.scopeAnchor.role;
      headers['X-Impersonate-Scope-Email'] = imp.scopeAnchor.email;
    }
  }

  const signal = fetchOpts.signal
    ?? (typeof AbortSignal?.timeout === 'function' ? AbortSignal.timeout(timeoutMs) : undefined);

  let res;
  try {
    res = await fetch(path, { ...fetchOpts, headers, signal });
  } catch (e) {
    if (e?.name === 'TimeoutError' || e?.name === 'AbortError') {
      throw new Error(`Request timed out (${path})`);
    }
    throw e;
  }
  const data = await res.json().catch(() => ({}));
  // Backstop: a 401 means the token MSAL handed us was rejected by the API
  // (expired/revoked mid-session). Trigger interactive re-auth rather than
  // stranding the UI on a data error with no path back to login.
  if (res.status === 401) {
    _reauth?.();
    throw new Error(data.error || 'Session expired — signing in again…');
  }
  if (!res.ok) {
    const e = new Error(data.error || `API error ${res.status}`);
    e.status = res.status;
    e.data = data;
    e.code = data.code || null;
    if (e.code === SUB_SESSION_EXPIRED) announceSubSessionExpired();
    throw e;
  }
  return data;
}
